import { useRef } from 'react';

import { InsertImageShortcut } from 'icons';
import { ReactEditor, useSlate } from 'slate-react';
import { twMerge } from 'tailwind-merge';
import { Tooltip } from 'ui';
import { IconButton } from 'ui/atoms';

import { isElementActive } from '../utils';

type InsertImageButtonProps = {
  title: string;
  hidden?: boolean;
  onClose?: () => void;
};

const InsertImageButton = ({ title, hidden, onClose }: InsertImageButtonProps) => {
  const editor = useSlate();
  const inputRef = useRef<HTMLInputElement>(null);
  const isActive = isElementActive(editor, 'image');

  // Same path as pasting images from the clipboard: compression + the editor's uploader.
  const insertFiles = (files: FileList | null) => {
    if (files?.length) {
      const data = new DataTransfer();
      Array.from(files).forEach((file) => data.items.add(file));
      ReactEditor.focus(editor);
      editor.insertData(data);
    }
    if (inputRef.current) inputRef.current.value = '';
    onClose?.();
  };

  return (
    <div>
      <Tooltip title={title} placement="top">
        <div className="relative">
          <IconButton
            open={isActive}
            className={twMerge(
              'flex items-center h-9 w-full hover:bg-transparent active:bg-v2blueGray-100',
              !hidden && 'justify-center w-9',
              hidden && 'justify-start px-2 gap-2 active:bg-v2blueGray-100 hover:bg-v2blueGray-100',
              isActive && 'bg-v2blueGray-100 hover:bg-v2blueGray-100',
            )}
            onMouseDown={(e) => {
              e.preventDefault();
              inputRef.current?.click();
            }}
          >
            <InsertImageShortcut className="w-7 h-7" />
          </IconButton>
        </div>
      </Tooltip>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => insertFiles(e.target.files)}
      />
    </div>
  );
};

export { InsertImageButton };
